import type { ComponentProps, ReactNode } from "react";
import { Breadcrumb, useScrolledPastTop } from "@wags/tanstack-start/browser";
import { cn } from "@wags/ui/ui-utils";
import {
    DOG_CONTENT_COLUMN,
    DOG_DESKTOP_GRID,
    DOG_HEADER_VARS,
} from "./dog-desktop-layout";

export function DogDesktopHeader({
    breadcrumbItems,
    navigation,
    hideBreadcrumb = false,
    showSummary,
    title,
    children,
}: {
    breadcrumbItems: ComponentProps<typeof Breadcrumb>["items"];
    navigation: ReactNode;
    hideBreadcrumb?: boolean;
    showSummary: boolean;
    title: ReactNode;
    children: ReactNode;
}) {
    const scrolled = useScrolledPastTop();

    return (
        <div
            className={cn(
                "pointer-events-none fixed inset-x-0 top-0 z-50 hidden w-full dog-desktop:block",
                DOG_HEADER_VARS,
            )}
        >
            <div className="pointer-events-auto bg-true-white">
                {navigation}
            </div>
            <div
                className={cn(
                    DOG_DESKTOP_GRID,
                    hideBreadcrumb && !showSummary && "invisible",
                )}
            >
                <div
                    className={cn(
                        "pointer-events-auto min-w-0 bg-true-white dog-desktop:-mx-4 dog-desktop:px-4",
                        DOG_CONTENT_COLUMN,
                    )}
                >
                    <div
                        className={cn(
                            "grid h-[var(--dog-context-height)] items-center border-b border-transparent transition-colors duration-150 motion-reduce:transition-none",
                            (scrolled || showSummary) &&
                                "border-doberman-black/8",
                        )}
                    >
                        <div
                            inert={showSummary}
                            aria-hidden={showSummary}
                            className={cn(
                                "col-start-1 row-start-1 min-w-0 transition-[opacity,visibility] duration-150 ease-out motion-reduce:transition-none",
                                showSummary && "invisible opacity-0",
                            )}
                        >
                            <Breadcrumb
                                items={breadcrumbItems}
                                hideScrollBorder
                                className="max-w-none p-0"
                            />
                        </div>
                        <div
                            inert={!showSummary}
                            aria-hidden={!showSummary}
                            className={cn(
                                "col-start-1 row-start-1 flex min-w-0 items-center justify-between gap-4 transition-[opacity,visibility] duration-150 ease-out motion-reduce:transition-none",
                                !showSummary && "invisible opacity-0",
                            )}
                        >
                            <p className="min-w-0 truncate text-body text-doberman-black">
                                {title}
                            </p>
                            <div className="flex shrink-0 items-center gap-4">
                                {children}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
